import { lazy } from "react";

/**
 * Единственная точка входа во второй слой из остального приложения.
 *
 * ⚠️ Статических импортов из `features/exercises/*` за пределами этого файла
 * быть не должно, кроме типов. За слоем по цепочке едет KaTeX со шрифтами, и
 * один такой импорт утащил бы его в чанк комнаты урока транзитивно, а заметить
 * это можно было бы только замером итоговой сборки. Прецедент записан в
 * `plans/plan-slides.ts`: там ровно так утащило бы Yjs.
 */

type LayerModule = typeof import("./ExerciseLayer.tsx");

let pending: Promise<LayerModule> | null = null;

/**
 * Прогрев чанка по факту, а не по объявлению.
 *
 * Зовётся, когда упражнение появилось в окне предзагрузки («текущий слайд и
 * два следующих»), тем же окном, которым греется медиа. Предмета у заготовки
 * нет и не будет (раздел «палитра общая» проекта), поэтому единственный
 * честный признак это наличие элемента на ближайших слайдах.
 */
export function loadExerciseLayer(): Promise<LayerModule> {
  pending ??= import("./ExerciseLayer.tsx");
  return pending;
}

export const LazyExerciseLayer = lazy(() =>
  loadExerciseLayer().then((m) => ({ default: m.ExerciseLayer })),
);

type AnswersApiModule = typeof import("./answers-api.ts");

let answersApi: Promise<AnswersApiModule> | null = null;

/**
 * Фетчеры проверки ответов.
 *
 * ⚠️ Только так, и никогда прямым импортом, даже из комнаты урока. За
 * `answers-api.ts` едет `@edu/shared/checking` (форма эталона и сравнение
 * ответа с ним), а комната это общий чанк: один статический импорт вынес бы
 * подпуть туда, и заметить это можно было бы только списком файлов сборки.
 *
 * Обёртка при этом та же, что у слоя: обещание запоминается, повторный вызов
 * не тянет чанк второй раз.
 */
export function loadAnswersApi(): Promise<AnswersApiModule> {
  answersApi ??= import("./answers-api.ts");
  return answersApi;
}

/**
 * Лента решения домашки (ученик) и экран результатов (преподаватель).
 *
 * ⚠️ Оба в чанке упражнений и достаются только отсюда: за ними едут
 * `@edu/shared/checking` (форма эталона, сравнение) и KaTeX, которым в
 * стартовом чанке не место. Страница `/homework/:id` из стартового чанка
 * рисует только обвязку и грузит эти компоненты через Suspense.
 */
export const LazyHomeworkFeed = lazy(() =>
  import("./HomeworkFeed.tsx").then((m) => ({ default: m.HomeworkFeed })),
);

export const LazyHomeworkResults = lazy(() =>
  import("./HomeworkResults.tsx").then((m) => ({ default: m.HomeworkResults })),
);

/**
 * Палитра и редактор свойств.
 *
 * ⚠️ Отдельный чанк от слоя отрисовки, и разделение здесь не по весу, а по
 * тому, кто что грузит. За редактором едут источники, эталоны и компиляция
 * (`@edu/shared/exercises`), а слой отрисовки грузит ученик. Один чанк на двоих
 * отдал бы ученику компиляцию вместе с условием.
 */
export const LazyExercisePalette = lazy(() =>
  import("./ExerciseEditor.tsx").then((m) => ({ default: m.ExercisePalette })),
);

export const LazyExerciseProperties = lazy(() =>
  import("./ExerciseEditor.tsx").then((m) => ({ default: m.ExerciseProperties })),
);

/**
 * Вычитка вопросов к аудированию (docs/listening-questions-design.md, 10.2).
 *
 * ⚠️ **Только отсюда, и это требование барьера, а не порядок.** Схема вопроса
 * несёт `correct`, то есть правильный ответ, поэтому панель, её фетчер, её слова
 * и сборка источников лежат за `@edu/shared/exercises` и дописаны в белый список
 * `answer-barrier.test.ts`. Кнопка, которая панель открывает, стоит у звукового
 * элемента слайда (`features/tts/TrackTranscript.tsx`), в белом списке её нет и
 * быть не должно: она умеет ровно одно, позвать этот `import()`.
 *
 * Обёртка та же, что у соседей: ленивый компонент, чанк тянется по первой
 * отрисовке, то есть по нажатию.
 */
export const LazyListeningQuestions = lazy(() =>
  import("./ListeningQuestions.tsx").then((m) => ({ default: m.ListeningQuestions })),
);

type ExtractPlaceModule = typeof import("./extract-place.ts");

let extractPlace: Promise<ExtractPlaceModule> | null = null;

/**
 * Постановка заданий из PDF на слайды (docs/exercise-extraction-design.md,
 * раздел 10).
 *
 * ⚠️ **Только отсюда, и это требование барьера.** Модуль собирает ИСТОЧНИК
 * задания с отмеченными верными вариантами и компилирует его, то есть ходит
 * за `@edu/shared/exercises`, и потому стоит в белом списке
 * `answer-barrier.test.ts`. Экран сверки лежит в чанке редактора, который
 * грузит каждый, кто открыл любую заготовку, и один статический импорт вынес
 * бы туда компиляцию. Обещание запоминается, как у соседей.
 */
export function loadExtractPlace(): Promise<ExtractPlaceModule> {
  extractPlace ??= import("./extract-place.ts");
  return extractPlace;
}

type ScanPlaceModule = typeof import("./scan-place.ts");

let scanPlace: Promise<ScanPlaceModule> | null = null;

/**
 * Постановка заданий со скана вместе с картинками и общим текстом
 * (`docs/scan-extraction-design.md`, решение 8 и раздел 13).
 *
 * ⚠️ **Только отсюда, по той же причине, что `loadExtractPlace`.** Модуль
 * разбирает упражнения скана (в них ответы модели) и собирает их задания,
 * то есть ходит за `@edu/shared/exercises`, и стоит в белом списке
 * `answer-barrier.test.ts`. Обёртка добавления (`plans/scan-pictures-insert.ts`)
 * получает его зависимостью и знает о нём только типы.
 */
export function loadScanPlace(): Promise<ScanPlaceModule> {
  scanPlace ??= import("./scan-place.ts");
  return scanPlace;
}

type ScanCardModule = typeof import("./scan-card.ts");

let scanCard: Promise<ScanCardModule> | null = null;

/**
 * Карточки упражнений скана на сверке (`docs/scan-extraction-design.md`, раздел
 * 4, «Карточка скана»).
 *
 * ⚠️ **Только отсюда, по той же причине, что `loadScanPlace`.** Модуль разбирает
 * упражнения скана схемой `ScanTask` и отдаёт карточке предложенные моделью
 * ответы, то есть ходит за `@edu/shared/exercises`, и стоит в белом списке
 * `answer-barrier.test.ts`. Окно сверки знает о нём только типы.
 */
export function loadScanCard(): Promise<ScanCardModule> {
  scanCard ??= import("./scan-card.ts");
  return scanCard;
}

export type { CreatedExercise } from "./ExerciseEditor.tsx";
